import { Box, Card, CardActionArea, Chip, Link, Tooltip, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import {
  JobOpportunity,
  COMPANY_TIER_LABELS,
  COMPANY_TIER_CHIP_COLOR,
  resolveCompanyTier,
  fitScoreColor,
} from '../../state/jobOpportunities'
import { Contact } from '../../services/api'
import { ensureHttpProtocol } from '../../utils/validation'
import InactivityChip from './InactivityChip'
import WarmContactsCell, { contactsForJob } from './WarmContactsCell'

/**
 * One application as a card, for widths where the table cannot stay a table.
 *
 * Below 768px a seven-column board either scrolls sideways or drops columns,
 * and both lose data the staff member came for. The card carries every field
 * the desktop row carries — client, tier, fit, decision, location, warm
 * contacts, applied date, interview stage, the inactivity warning — so moving
 * to a phone costs layout, never information.
 *
 * The cells themselves are not re-implemented here. `decision` and
 * `interviewStage` arrive already rendered by the page, so the two surfaces
 * cannot drift into showing the same value differently.
 *
 * Each optional field is opt-in rather than opt-out, because this renders in
 * the client portal too and the portal's table deliberately carries neither
 * warm contacts nor a client name. A default-on prop would have quietly shown
 * a client their own network on a surface that was decided against.
 *
 * `CardActionArea` rather than a click handler on the Card: the row opens a
 * dialog, so it has to be a real button that Tab reaches and Enter fires.
 */
export default function JobApplicationCard({
  job,
  contacts,
  showClient,
  showInactivity,
  showWarmContacts,
  decision,
  interviewStage,
  appliedLabel,
  onOpen,
}: {
  job: JobOpportunity
  contacts: Contact[]
  showClient?: boolean
  showInactivity?: boolean
  showWarmContacts?: boolean
  decision?: React.ReactNode
  interviewStage?: React.ReactNode
  appliedLabel: string
  onOpen: () => void
}) {
  const tier = resolveCompanyTier(job.companyTier)

  // A table cell can render a bare em-dash and still be readable, because the
  // column header says what is missing. On a card there is no header, so an
  // empty value has to be left out rather than drawn as a dash sitting next to
  // another dash.
  const warmCount = showWarmContacts
    ? contactsForJob(contacts, job.company, job.linkedContactIds).length
    : 0
  const hasMeta = Boolean(decision || interviewStage || warmCount > 0)

  return (
    <Card>
      <CardActionArea onClick={onOpen} sx={{ p: 1.5, alignItems: 'stretch' }}>
        {showClient && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {job.clientName || '—'}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                {job.company}
              </Typography>
              <Chip
                label={COMPANY_TIER_LABELS[tier]}
                size="small"
                color={COMPANY_TIER_CHIP_COLOR[tier]}
                sx={{ height: 18, typography: 'badge' }}
              />
            </Box>
            {job.url ? (
              <Link
                href={ensureHttpProtocol(job.url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}
              >
                <Typography variant="body2" component="span">
                  {job.jobTitle}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {job.jobTitle}
              </Typography>
            )}
          </Box>

          {job.fitScore != null ? (
            <Tooltip title={job.fitScoreReason || 'Job fit score'}>
              <Chip
                label={job.fitScore}
                size="small"
                sx={{
                  flexShrink: 0,
                  backgroundColor: fitScoreColor(job.fitScore),
                  color: 'white',
                  fontWeight: 700,
                }}
              />
            </Tooltip>
          ) : null}
        </Box>

        {(hasMeta || showInactivity) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
            {decision}
            {interviewStage}
            {warmCount > 0 && (
              <WarmContactsCell
                contacts={contacts}
                company={job.company}
                linkedContactIds={job.linkedContactIds}
              />
            )}
            {showInactivity && <InactivityChip job={job} />}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1.25,
            pt: 1.125,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <PlaceOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 400, flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {job.location || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, flexShrink: 0 }}>
            {appliedLabel}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  )
}
